import { useCallback, useEffect, useRef, useState, type CSSProperties } from "react";
import { Link } from "react-router";
import { BackLink } from "../components/ui/BackLink";
import { Icon } from "../components/ui/Icon";
import { ApiError } from "../lib/api";
import { lessonsApi } from "../lib/lessonsApi";
import { libraryApi } from "../lib/libraryApi";
import { useToast } from "./goal/useToast";
import { libLoad, Tile, verOf } from "./library/ShelfParts";
import { CHANNELS, STAGES, dayOf, labelOf, matches, shelfItems, type ShelfItem } from "./library/shelf";
import { Compare, type Ver } from "./versions/Versions";
import { useNative } from "../../shared/phone/media";
import { LibrarySplit } from "./library/LibraryNative";
import { useCan } from "../context/AuthContext";
import "../styles/library.css";
import "../styles/shelf.css";

/** The Library (`/library`): what the agent follows. On a phone or an iPad, the native shelf; else the page. */
export function Library() {
  const native = useNative();
  return native ? <LibrarySplit sel={null} detail={null} /> : <Shelf />;
}

/** The page on a computer: the shelf. Skills by channel, tools by the stage of a goal. */
function Shelf() {
  const [items, setItems] = useState<ShelfItem[] | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [q, setQ] = useState("");
  const [waiting, setWaiting] = useState<number | null>(null);
  const [cmp, setCmp] = useState<{ it: ShelfItem; from: Ver; to: Ver } | null>(null);
  const [busy, setBusy] = useState(false);
  const find = useRef<HTMLInputElement>(null);
  const { toast, say } = useToast();
  const may = useCan()("editor");

  const load = useCallback(async () => {
    try {
      setItems(shelfItems((await libraryApi.list()).items));
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : String(e));
    }
  }, []);
  useEffect(() => {
    void load();
    lessonsApi.list().then(
      (r) => setWaiting(r.lessons.filter((l) => l.state === "proposed").length),
      () => setWaiting(null),
    );
  }, [load]);
  useEffect(() => {
    const key = (e: KeyboardEvent) => {
      if (e.key === "/" && !(e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement)) {
        e.preventDefault();
        find.current?.focus();
      }
    };
    addEventListener("keydown", key);
    return () => removeEventListener("keydown", key);
  }, []);

  const act = async (run: () => Promise<unknown>, done: string) => {
    setBusy(true);
    try {
      await run();
      setCmp(null);
      say(done);
      await load();
    } catch (e) {
      say(e instanceof ApiError ? e.message : "That did not work. Try again.", false);
    } finally {
      setBusy(false);
    }
  };
  const update = (it: ShelfItem) => act(() => libraryApi.takeDefault(it.inUse.id), `Updated to Flashadz v${it.waits?.n}. New goals get it from now.`);
  const keep = (it: ShelfItem) => act(() => libraryApi.keepMine(it.inUse.id), `Kept ${labelOf(it.inUse).toLowerCase()}. Flashadz v${it.waits?.n} stops waiting.`);

  const all = items ?? [];
  const waits = all.filter((it) => it.waits);
  const shown = all.filter((it) => matches(it, q));
  const skills = CHANNELS.map((c) => ({ title: c.title, its: c.names.flatMap((n) => shown.filter((it) => it.kind === "skill" && it.name === n)) }));
  const otherSkills = shown.filter((it) => it.kind === "skill" && !CHANNELS.some((c) => c.names.includes(it.name)));
  if (otherSkills.length) skills.push({ title: "Other", its: otherSkills });
  const stages = STAGES.map((s, i) => ({ title: s.title, when: s.when, i, its: s.names.flatMap((n) => shown.filter((it) => it.kind === "method" && it.name === n)) }));
  const otherTools = shown.filter((it) => it.kind === "method" && !STAGES.some((s) => s.names.includes(it.name)));
  if (otherTools.length) stages.push({ title: "Other jobs", when: "When the agent needs them", i: stages.length, its: otherTools });
  const sk = skills.filter((c) => c.its.length);
  const st = stages.filter((s) => s.its.length);
  const learned = !q.trim() || /lesson|learn/i.test(q);
  const own = all.filter((it) => it.inUse.who === "you");
  const fresh = all
    .flatMap((it) => it.versions.filter((v) => v.who === "flashadz"))
    .map((v) => v.at)
    .toSorted()
    .at(-1);

  return (
    <section className="wrap ga lb">
      <header className="ph">
        <div className="ctx">
          <BackLink to="/command" label="Command Center" />
        </div>
        <div className="ph-t">
          <h1>Library</h1>
          <div className="lb-find" role="search">
            <Icon name="search" />
            <input ref={find} type="search" placeholder="Find by name" aria-label="Find by name" autoComplete="off" value={q} onChange={(e) => setQ(e.target.value)} />
            <kbd aria-hidden="true">/</kbd>
          </div>
        </div>
        <p className="ph-s">What your agent follows. New goals copy it on day one.</p>
        {items && !waits.length && (
          <p className="lb-state">
            <Icon name="check" />
            <span>
              {own.length ? (
                <>
                  <b>
                    {own.length === 1 ? "One is your own version" : `${own.length} are your own versions`}: {own.map((it) => it.title).join(", ")}.
                  </b>{" "}
                  The rest is Flashadz's latest. Nothing waits for you.
                </>
              ) : (
                <>
                  <b>Everything is Flashadz's latest{fresh ? `, from ${dayOf(fresh)}` : ""}.</b> Nothing waits for you.
                </>
              )}
            </span>
          </p>
        )}
      </header>
      {err && <p className="load-note">Couldn't load the library: {err}</p>}
      {!items && !err && <p className="load-note">Loading…</p>}
      {items && (
        <>
          {waits.length > 0 && !q.trim() && (
            <section className="lb-wait" aria-labelledby="lb-wh">
              <h2 className="sr" id="lb-wh">
                Waits for you
              </h2>
              {waits.map((it) => (
                <div className="lb-wrow" key={it.key}>
                  <span className="ki">
                    <Icon name={it.icon} />
                  </span>
                  <div>
                    <span className="lb-weye">Waits for you</span>
                    <b className="lb-wt">
                      <Link to={`/library/${encodeURIComponent(it.inUse.id)}`}>
                        {it.title}: Flashadz v{it.waits?.n} is out
                      </Link>
                    </b>
                    {it.waits?.note && <p className="lb-wl">{it.waits.note}</p>}
                  </div>
                  <div className="lb-wa">
                    <button type="button" className="btn sm ghost" onClick={() => it.waits && setCmp({ it, from: verOf(it.inUse), to: verOf(it.waits) })}>
                      What changed
                    </button>
                    {may && (
                      <>
                        <button type="button" className="btn sm ghost" disabled={busy} onClick={() => void keep(it)}>
                          Keep {labelOf(it.inUse).toLowerCase()}
                        </button>
                        <button type="button" className="btn sm" disabled={busy} onClick={() => void update(it)}>
                          Update to v{it.waits?.n}
                        </button>
                      </>
                    )}
                  </div>
                </div>
              ))}
            </section>
          )}
          {sk.length > 0 && (
            <section className="lb-shelf" aria-labelledby="sh-sk">
              <div className="lb-sh">
                <h2 id="sh-sk">Skills</h2>
                <p>What the agent knows, by channel</p>
                <p className="lb-key">
                  Each mark is a version:
                  <span className="nt" aria-hidden="true">
                    <i className="on" />
                  </span>
                  Flashadz's
                  <span className="nt" aria-hidden="true">
                    <i className="y on" />
                  </span>
                  yours
                  <span className="nt" aria-hidden="true">
                    <i className="w" />
                  </span>
                  waits for you
                </p>
              </div>
              <div className="lb-sk">
                {sk.map((c) => (
                  <div className={`lb-cl${c.its.length > 1 ? " n2" : ""}`} style={{ "--n": c.its.length } as CSSProperties} key={c.title}>
                    <div className="lb-cl-h">{c.title}</div>
                    <div className="lb-cl-g">
                      {c.its.map((it) => (
                        <Tile it={it} key={it.key} />
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </section>
          )}
          {st.length > 0 && (
            <section className="lb-shelf" aria-labelledby="sh-me">
              <div className="lb-sh">
                <h2 id="sh-me">Tools</h2>
                <p>How it does each job, in the order a goal meets them</p>
              </div>
              <ol className="lb-stages" style={{ "--rows": 1 + Math.max(...st.map((s) => s.its.length)) } as CSSProperties}>
                {st.map((s) => (
                  <li className="lb-stg" key={s.title}>
                    <div className="lb-stg-h">
                      <span className="lb-num">{s.i + 1}</span>
                      <h3>{s.title}</h3>
                      <p className="lb-when">{s.when}</p>
                    </div>
                    {s.its.map((it) => (
                      <Tile it={it} key={it.key} />
                    ))}
                  </li>
                ))}
              </ol>
            </section>
          )}
          {learned && (
            <section className="lb-shelf" aria-labelledby="sh-le">
              <div className="lb-sh">
                <h2 id="sh-le">
                  <Link to="/library/lessons">Lessons</Link>
                </h2>
                <p>What it has learned from each goal's results</p>
              </div>
              <div className="lb-empty">
                <span className="ki">
                  <Icon name="bulb" />
                </span>
                <p>
                  {waiting ? (
                    <>
                      <b>
                        {waiting} {waiting === 1 ? "lesson waits" : "lessons wait"} for your OK.
                      </b>{" "}
                      See what each goal learned.
                    </>
                  ) : (
                    <>
                      <b>Nothing waits.</b> Anything a goal learns waits here for your OK.
                    </>
                  )}
                </p>
              </div>
            </section>
          )}
          {!sk.length && !st.length && !learned && (
            <div className="lb-none">
              <b>Nothing matches "{q.trim()}".</b>
            </div>
          )}
        </>
      )}
      {cmp && (
        <Compare
          versions={cmp.it.versions.map(verOf)}
          from={cmp.from}
          to={cmp.to}
          load={libLoad}
          onPick={(from, to) => setCmp({ ...cmp, from, to })}
          onClose={() => setCmp(null)}
          foot={
            may && cmp.it.waits && cmp.to.id === cmp.it.waits.id ? (
              <>
                <span className="note">Nothing updates until you say so.</span>
                <button type="button" className="btn sm ghost" disabled={busy} onClick={() => void keep(cmp.it)}>
                  Keep {labelOf(cmp.it.inUse).toLowerCase()}
                </button>
                <button type="button" className="btn sm" disabled={busy} onClick={() => void update(cmp.it)}>
                  Update to v{cmp.it.waits.n}
                </button>
              </>
            ) : undefined
          }
        />
      )}
      {toast}
    </section>
  );
}
